import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, onSnapshot, getDocs } from "firebase/firestore"; // Added getDocs
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import type { RecoveryLessonProgress } from "../../types/recovery";

export function useUserProgress() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const queryKey = useMemo(() => ["user-progress", user?.uid], [user?.uid]);

  // 1. Updated useQuery to perform an initial fetch
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user?.uid) return {};

      // Perform a one-time fetch of the progress subcollection
      const progressRef = collection(db, "users", user.uid, "progress");
      const snapshot = await getDocs(progressRef);
      
      const map: Record<string, RecoveryLessonProgress> = {};
      snapshot.forEach((doc) => {
        const v = doc.data();
        map[doc.id] = {
          lessonId: doc.id,
          percentage: v.percentage ?? 0,
          status: v.status ?? "not_started",
        };
      });

      return map; 
    },
    enabled: !!user,
    // Keep data fresh forever in the cache; let onSnapshot handle updates
    staleTime: Infinity, 
  });

  // 2. The Real-time Sync Logic (remains mostly the same)
  useEffect(() => {
    if (!user?.uid) return;

    const progressRef = collection(db, "users", user.uid, "progress");

    const unsubscribe = onSnapshot(progressRef, (snapshot) => {
      const map: Record<string, RecoveryLessonProgress> = {};
      
      snapshot.forEach((doc) => {
        const v = doc.data();
        map[doc.id] = {
          lessonId: doc.id,
          percentage: v.percentage ?? 0,
          status: v.status ?? "not_started",
        };
      });

      // Update the cache manually when Firestore pushes a change
      queryClient.setQueryData(queryKey, map);
    }, (error) => {
      console.error("Firestore Progress Listener Error:", error);
    });

    return () => unsubscribe();
  }, [user?.uid, queryClient, queryKey]);

  return query;
}

export const USER_PROGRESS_KEY = (uid: string) => ["user-progress", uid];